import { api, BANK_API_SUB_URLS, V2 } from '@/api';
import { get } from 'lodash';
import { AxiosResponse } from 'axios';
import { Module } from 'vuex';
import { RootState } from '@/@types/member';
import { MemberBankAccountResponseModel } from '@/@types/member';

interface BankingState {
  bankAccounts: MemberBankAccountResponseModel[];
}

const BANKING_STATE: Module<BankingState, RootState> = {
  state: {
    bankAccounts: [],
  },
  actions: {
    // UC-REST-021 - Get Banking Information
    getBankingInformation(context): Promise<AxiosResponse> {
      return api
        .get(
          `${BANK_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/bank-accounts${V2}`,
        )
        .then((bankAccounts) => {
          context.commit('setBankAccounts', bankAccounts.data);
          return bankAccounts.data;
        });
    },
    // UC-REST-022 - Update Bank Account
    updateBankAccount(context, options): Promise<AxiosResponse> {
      const BANK_KEY = options.bankKey;
      return api
        .patch(
          `${BANK_API_SUB_URLS.bankAccounts}/${BANK_KEY}`,
          {
            isTerminated: options.isTerminated,
            useForClaims: options.useForClaims,
            useForContributions: options.useForContributions,
            callingApplication: get(options, 'callingApplication', ''),
          },
        )
        .then((bankAccount) => {
          return bankAccount.data;
        });
    },
    // UC-REST-023 - Add Bank Account
    addBankAccount(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${BANK_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/bank-accounts`,
          options,
        )
        .then((bankAccount) => {
          return bankAccount.data;
        });
    },
    // UC-REST-024 - Validate Routing Number
    validateRoutingNumber(context, routingNumber): Promise<AxiosResponse> {
      return api
        .get(
          `${BANK_API_SUB_URLS.banks}/${routingNumber}`,
        )
        .then((routingNumberData) => {
          return routingNumberData.data;
        });
    },
  },
  mutations: {
    setBankAccounts(
      state: BankingState,
      data: MemberBankAccountResponseModel[],
    ) {
      state.bankAccounts = data ? data : [];
    },
  },
  getters: {
    bankAccounts(state): MemberBankAccountResponseModel[] {
      return state.bankAccounts;
    },
    bankAccountsForDeposit(state): MemberBankAccountResponseModel[] {
      const ACCOUNTS = state.bankAccounts.filter((account) => {
        return account.useForContributions;
      });

      return ACCOUNTS.sort((a, b) => {
        if (a.useForClaims && !b.useForClaims) {
          return -1;
        } else if (!a.useForClaims && b.useForClaims) {
          return 1;
        } else {
          if (a.name !== b.name) {
            // @ts-ignore
            return a.name - b.name;
          } else {
            // @ts-ignore
            return a.accountNumber - b.accountNumber;
          }
        }
      });
    },
    bankAccountsSorted(state): MemberBankAccountResponseModel[] {
      const BANK_ACCOUNT = state.bankAccounts;
      const BANK_ACCOUNT_SORTED_ARRAY = BANK_ACCOUNT.slice(0);
      let primaryBankAccountIndex;

      primaryBankAccountIndex = BANK_ACCOUNT.findIndex((bankAccount) => {
        return bankAccount.useForClaims;
      });

      if (primaryBankAccountIndex > -1) {
        BANK_ACCOUNT_SORTED_ARRAY.splice(primaryBankAccountIndex, 1);
      }

      if (BANK_ACCOUNT_SORTED_ARRAY.length > 1) {
        BANK_ACCOUNT_SORTED_ARRAY.sort((a, b) => {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          if (a.name.toLowerCase() === b.name.toLowerCase()) {
            // @ts-ignore
            return a.accountNumber - b.accountNumber;
          }
        });
      }

      if (primaryBankAccountIndex > -1) {
        BANK_ACCOUNT_SORTED_ARRAY.unshift(
          BANK_ACCOUNT[primaryBankAccountIndex],
        );
      }

      return BANK_ACCOUNT_SORTED_ARRAY;
    },
    bankAccountQuantity(state): number {
      return state.bankAccounts.length;
    },
    primaryBankAccount(state): MemberBankAccountResponseModel {
      const PRIMARY_ACCOUNT_INDEX = state.bankAccounts.findIndex(
        (bankAccount) => {
          return bankAccount.useForClaims;
        },
      );
      return PRIMARY_ACCOUNT_INDEX > -1
        ? state.bankAccounts[PRIMARY_ACCOUNT_INDEX]
        : null;
    },
  },
};

export default BANKING_STATE;
